import Phaser from 'phaser';
import { findPath, adjacentWalkable } from '../world/pathfinding';
import { faceMove, faceIdle, type Facing } from '../world/playerFacing';
import { trailSign } from '../world/trailSign';
import { TX, AUDIO } from '../registry/keys';
import { CONFIG } from '../data/config';
import { playSfx } from '../audio/sfx';
import { setOcean } from '../audio/synth';
import { store } from '../state/store';
import { getItem } from '../data/items';
import { tileKey, type TileXY } from '../types';
import { getHat, hatTexture } from '../data/hats';
import type { UIScene } from './UIScene';

const TS = CONFIG.tileSize;
const BW = 12;
const BH = 9;
const EXIT: TileXY = { x: 6, y: 8 }; // trail back to the forest
const ENTRY: TileXY = { x: 6, y: 7 };
const ORIGIN_Y = 0.7;
const OCEAN_ROWS = 2; // top rows are ocean (blocking)

interface Spot {
  x: number;
  y: number;
  item: string;
  tex: string;
}
/** Beach forage spots (flags 'forageb*', cleared each morning with the forest). */
const FORAGE: Spot[] = [
  { x: 2, y: 3, item: 'shell', tex: TX.shell },
  { x: 9, y: 3, item: 'shell', tex: TX.shell },
  { x: 4, y: 5, item: 'starfish', tex: TX.starfish },
  { x: 7, y: 5, item: 'starfish', tex: TX.starfish },
  { x: 5, y: 3, item: 'seaweed', tex: TX.seaweed },
  { x: 8, y: 6, item: 'seaweed', tex: TX.seaweed },
];

/** The beach: sand + ocean, forage shells/starfish/seaweed, trail back to the forest. */
export class BeachScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private blocked = new Set<string>();
  private path: TileXY[] = [];
  private fishing: {
    bobber: Phaser.GameObjects.Image;
    water: TileXY;
    biting: boolean;
    biteEvent?: Phaser.Time.TimerEvent;
    missEvent?: Phaser.Time.TimerEvent;
  } | null = null;
  private pendingArrival: (() => void) | null = null;
  private playerTile: TileXY = { ...ENTRY };
  private leaving = false;
  private hat?: Phaser.GameObjects.Image;
  private lastFacing: Facing = 'down';
  private spotSprites = new Map<number, Phaser.GameObjects.Image>();
  private beachPet?: Phaser.GameObjects.Image;

  constructor() {
    super('Beach');
  }

  private get ui(): UIScene {
    return this.scene.get('UI') as UIScene;
  }

  create(): void {
    this.leaving = false;
    this.blocked.clear();
    this.path = [];
    this.spotSprites.clear();
    this.cameras.main.setBackgroundColor('#bfe0e8');
    const short = Math.min(this.scale.width, this.scale.height);
    this.cameras.main.setZoom(Phaser.Math.Clamp(Math.round(short / (BW * TS)), CONFIG.minZoom, CONFIG.maxZoom));
    this.cameras.main.centerOn((BW * TS) / 2, (BH * TS) / 2);

    this.buildBeachMap();

    this.player = this.add.sprite(ENTRY.x * TS + TS / 2, ENTRY.y * TS + TS, TX.player, 0).setOrigin(0.5, ORIGIN_Y);
    this.player.setDepth(ENTRY.y * TS + TS);
    this.playerTile = { ...ENTRY };
    this.applyHat();

    this.add
      .text(this.scale.width / 2, 8, 'Beach — forage, then tap the trail back', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#2c5a6a',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setResolution(2)
      .setDepth(1000);

    this.input.on('pointerdown', this.onTap, this);
    this.buildWaves();
    setOcean(true);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => setOcean(false));
    this.time.addEvent({ delay: 9000, loop: true, callback: () => { if (Math.random() < 0.5) this.spawnGull(); } });
    this.maybeSpawnBeachPet();
    this.time.addEvent({
      delay: 5000,
      loop: true,
      callback: () => {
        if (Math.random() < 0.5) playSfx(this, AUDIO.splash);
      },
    });
  }

  private buildBeachMap(): void {
    for (let y = 0; y < BH; y++) {
      for (let x = 0; x < BW; x++) {
        if (y < OCEAN_ROWS) {
          this.add.image(x * TS, y * TS, TX.ocean).setOrigin(0, 0).setDepth(0);
          this.blocked.add(tileKey(x, y)); // can't walk into the sea
        } else {
          this.add.image(x * TS, y * TS, TX.sand).setOrigin(0, 0).setDepth(0);
        }
      }
    }
    this.add.image(EXIT.x * TS, EXIT.y * TS, TX.trailArch).setOrigin(0, 0).setDepth(1);
    trailSign(this, EXIT.x, EXIT.y - 1, '🌲 Forest');

    FORAGE.forEach((s, i) => {
      const taken = !!store.get().flags['forageb' + i];
      const img = this.add
        .image(s.x * TS + TS / 2, s.y * TS + TS, s.tex)
        .setOrigin(0.5, 1)
        .setDepth(s.y * TS + TS)
        .setAlpha(taken ? 0.3 : 1);
      this.spotSprites.set(i, img);
      this.blocked.add(tileKey(s.x, s.y));
    });
  }

  private isBlocked = (x: number, y: number): boolean =>
    x < 0 || y < 0 || x >= BW || y >= BH || this.blocked.has(tileKey(x, y));

  private onTap(pointer: Phaser.Input.Pointer): void {
    if (this.leaving || this.ui.isModalOpen() || this.ui.consumesPointer(pointer.x, pointer.y)) return;
    if (this.fishing) {
      this.oceanTap(); // a line's already out — reel it in
      return;
    }
    const tile = { x: Math.floor(pointer.worldX / TS), y: Math.floor(pointer.worldY / TS) };
    if (tile.x < 0 || tile.y < 0 || tile.x >= BW || tile.y >= BH) return;

    const idx = FORAGE.findIndex((s) => s.x === tile.x && s.y === tile.y);
    if (idx >= 0) return this.routeToInteractable(tile, () => this.forage(idx));
    if (tile.x === EXIT.x && tile.y === EXIT.y) return this.routeTo(tile, () => this.leave());
    if (tile.y < OCEAN_ROWS) return this.routeToInteractable(tile, () => this.castOcean(tile));
    if (!this.isBlocked(tile.x, tile.y)) this.routeTo(tile, null);
  }

  private actionBob(): void {
    this.player.setScale(1, 1);
    this.tweens.add({ targets: this.player, scaleX: 1.12, scaleY: 0.85, duration: 95, yoyo: true, ease: 'Quad.easeOut', onComplete: () => this.player.setScale(1, 1) });
  }

  private castOcean(water: TileXY): void {
    if (this.fishing) return;
    if (!store.trySpendEnergy('fish')) {
      this.ui.showToast('Too tired to fish', 'warn');
      return;
    }
    this.actionBob();
    const bx = water.x * TS + TS / 2;
    const by = water.y * TS + TS / 2;
    const bobber = this.add.image(bx, by, TX.bobber).setDepth(by + 20);
    this.tweens.add({ targets: bobber, y: by - 2, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    this.fishing = { bobber, water, biting: false };
    playSfx(this, AUDIO.splash);
    this.ui.showToast('Fishing… wait for a bite!', 'info');
    this.fishing.biteEvent = this.time.delayedCall(Phaser.Math.Between(1600, 3600), () => this.oceanBite());
  }

  private oceanBite(): void {
    if (!this.fishing) return;
    this.fishing.biting = true;
    playSfx(this, AUDIO.pop);
    const b = this.fishing.bobber;
    const ex = this.add
      .text(b.x, b.y - 14, '!', { fontFamily: 'monospace', fontSize: '14px', color: '#ffe9a8' })
      .setOrigin(0.5)
      .setDepth(b.depth + 10)
      .setResolution(2);
    this.time.delayedCall(1400, () => ex.destroy());
    this.tweens.add({ targets: b, x: b.x + 2, duration: 70, yoyo: true, repeat: 10 });
    this.ui.showToast('Tap — it bit!', 'good');
    this.fishing.missEvent = this.time.delayedCall(2200, () => this.endOcean(false));
  }

  private oceanTap(): void {
    if (!this.fishing) return;
    this.endOcean(this.fishing.biting);
  }

  private endOcean(caught: boolean): void {
    if (!this.fishing) return;
    this.fishing.biteEvent?.remove();
    this.fishing.missEvent?.remove();
    this.tweens.killTweensOf(this.fishing.bobber);
    this.fishing.bobber.destroy();
    this.fishing = null;
    if (caught) {
      const fish = ['crab', 'squid', 'clam'][Math.floor(Math.random() * 3)]!;
      store.addItem(fish, 1);
      playSfx(this, AUDIO.harvest);
      store.logJournal('Caught my first fish 🎣');
      this.ui.showToast(`Caught a ${getItem(fish)?.name ?? fish}!`, 'good');
    } else {
      this.ui.showToast('It got away…', 'info');
    }
  }

  private forage(idx: number): void {
    if (store.get().flags['forageb' + idx]) {
      this.ui.showToast('Already foraged here today', 'info');
      return;
    }
    if (!store.trySpendEnergy('forage')) {
      this.ui.showToast('Too tired — rest up', 'warn');
      return;
    }
    this.actionBob();
    const spot = FORAGE[idx]!;
    store.update((s) => (s.flags['forageb' + idx] = true));
    store.addItem(spot.item, 1);
    playSfx(this, AUDIO.harvest);
    this.spotSprites.get(idx)?.setAlpha(0.3);
    this.ui.showToast(`+1 ${getItem(spot.item)?.name ?? spot.item}`, 'good');
  }

  private routeTo(tile: TileXY, action: (() => void) | null): void {
    if (this.playerTile.x === tile.x && this.playerTile.y === tile.y) {
      action?.();
      return;
    }
    const path = findPath(this.playerTile, tile, BW, BH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private routeToInteractable(target: TileXY, action: () => void): void {
    if (Math.abs(this.playerTile.x - target.x) + Math.abs(this.playerTile.y - target.y) <= 1) {
      action();
      return;
    }
    const adj = adjacentWalkable(target, this.playerTile, BW, BH, this.isBlocked);
    if (!adj) return;
    const path = findPath(this.playerTile, adj, BW, BH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  /** A line of foam at the shoreline that pulses in a travelling-wave pattern. */
  private buildWaves(): void {
    const sy = OCEAN_ROWS * TS;
    for (let x = 0; x < BW; x++) {
      const foam = this.add.rectangle(x * TS + TS / 2, sy, TS, 4, 0xffffff, 0.6).setDepth(2);
      this.tweens.add({
        targets: foam,
        y: sy - 3,
        alpha: 0.25,
        scaleX: 1.25,
        duration: 1100,
        yoyo: true,
        repeat: -1,
        delay: x * 90,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private spawnGull(): void {
    const dir = Math.random() < 0.5 ? 1 : -1;
    const y = Phaser.Math.Between(6, OCEAN_ROWS * TS - 6);
    const gull = this.add
      .image(dir > 0 ? -16 : BW * TS + 16, y, TX.bird)
      .setDepth(8000)
      .setScale(1.3)
      .setTint(0xffffff)
      .setFlipX(dir < 0);
    playSfx(this, AUDIO.chirp);
    this.tweens.add({
      targets: gull,
      x: dir > 0 ? BW * TS + 16 : -16,
      y: y + Phaser.Math.Between(-12, 12),
      duration: Phaser.Math.Between(5000, 8000),
      onComplete: () => gull.destroy(),
    });
  }

  /** The pet sometimes follows you to the beach; a dog stops to dig holes in the sand. */
  private maybeSpawnBeachPet(): void {
    if (Math.random() > 0.55) return;
    const spot = this.randomSand();
    if (!spot) return;
    const tex = store.get().petType === 'dog' ? TX.petDog : TX.petCat;
    this.beachPet = this.add.image(spot.x * TS + TS / 2, spot.y * TS + TS, tex).setOrigin(0.5, 1).setDepth(spot.y * TS + TS);
    this.time.addEvent({ delay: 2800, loop: true, callback: () => this.wanderBeachPet() });
    this.time.delayedCall(500, () => this.ui.showToast(`Your ${store.get().petType} followed you to the beach! 🐾`, 'info'));
  }

  private randomSand(): TileXY | null {
    for (let i = 0; i < 24; i++) {
      const x = Phaser.Math.Between(1, BW - 2);
      const y = Phaser.Math.Between(OCEAN_ROWS, BH - 2);
      if (!this.isBlocked(x, y)) return { x, y };
    }
    return null;
  }

  private wanderBeachPet(): void {
    if (!this.beachPet) return;
    const isDog = store.get().petType === 'dog';
    if (isDog && Math.random() < 0.4) {
      const dx = this.beachPet.x;
      const dy = this.beachPet.y;
      this.tweens.add({
        targets: this.beachPet,
        y: dy - 3,
        duration: 130,
        yoyo: true,
        repeat: 3,
        onYoyo: () => this.sandPoof(dx, dy - 2),
        onComplete: () => {
          playSfx(this, AUDIO.bark);
          if (Math.random() < 0.3) {
            store.addItem('shell', 1);
            this.ui.showToast('Your dog dug up a seashell! 🐚', 'good');
          }
        },
      });
      return;
    }
    const bx = Math.floor(this.beachPet.x / TS);
    const by = Math.floor((this.beachPet.y - 1) / TS);
    for (let i = 0; i < 6; i++) {
      const nx = Phaser.Math.Clamp(bx + Phaser.Math.Between(-2, 2), 1, BW - 2);
      const ny = Phaser.Math.Clamp(by + Phaser.Math.Between(-2, 2), OCEAN_ROWS, BH - 2);
      if (!this.isBlocked(nx, ny)) {
        this.beachPet.setFlipX(nx * TS + TS / 2 < this.beachPet.x);
        this.tweens.add({
          targets: this.beachPet,
          x: nx * TS + TS / 2,
          y: ny * TS + TS,
          duration: 900,
          ease: 'Sine.easeInOut',
          onUpdate: () => this.beachPet?.setDepth(this.beachPet.y),
        });
        if (Math.random() < 0.25) playSfx(this, isDog ? AUDIO.bark : AUDIO.meow);
        break;
      }
    }
  }

  private sandPoof(x: number, y: number): void {
    const p = this.add
      .particles(x, y, TX.dust, { speed: { min: 20, max: 60 }, angle: { min: 200, max: 340 }, scale: { start: 1.1, end: 0 }, tint: 0xe8d8a8, lifespan: 380, quantity: 4, emitting: false })
      .setDepth(9000);
    p.explode(4);
    this.time.delayedCall(500, () => p.destroy());
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    setOcean(false);
    playSfx(this, AUDIO.pop);
    this.scene.stop();
    this.scene.resume('Forest');
  }

  applyHat(): void {
    if (this.hat) {
      this.hat.destroy();
      this.hat = undefined;
    }
    const tex = hatTexture(getHat());
    if (tex) {
      this.hat = this.add.image(this.player.x, this.player.y - 16, tex).setOrigin(0.5, 0.5).setDepth(this.player.depth + 1);
    }
  }

  update(_time: number, delta: number): void {
    if (this.hat) {
      this.hat.setPosition(this.player.x, this.player.y - 16).setFlipX(this.player.flipX).setDepth(this.player.depth + 1);
    }
    if (this.path.length === 0) return;
    const target = this.path[0]!;
    const fx = target.x * TS + TS / 2;
    const fy = target.y * TS + TS;
    const dx = fx - this.player.x;
    const dy = fy - this.player.y;
    const dist = Math.hypot(dx, dy);
    this.lastFacing = faceMove(this, this.player, dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    if (dist <= step || dist === 0) {
      this.player.setPosition(fx, fy);
      this.player.setDepth(fy);
      this.playerTile = { x: target.x, y: target.y };
      this.path.shift();
      if (this.path.length === 0) {
        faceIdle(this, this.player, this.lastFacing);
        const a = this.pendingArrival;
        this.pendingArrival = null;
        a?.();
      }
    } else {
      this.player.x += (dx / dist) * step;
      this.player.y += (dy / dist) * step;
      this.player.setDepth(this.player.y);
    }
  }
}
